import { Divider, mergeClasses, Popover, PopoverSurface, Title3, tokens } from "@fluentui/react-components";
import TuneIcon from "~icons/material-symbols/tune";
import _ from "lodash";
import { memo, useMemo, useState } from "react";
import { DEFAULT_RANKING_OPTIONS } from "../../../constants";
import { useAppData } from "../../../hooks";
import { useSuggestionRankingFormStyles } from "../styles/SuggestionRankingForm.styles";
import { ActionButtons } from "./ActionButtons";
import { GenericPopoverPanel } from "./GenericPopoverPanel";
import { RenderRankingBooster } from "./RenderRankingBooster";

export type SuggestionRankingFormProps = {
  appId: string;
};

const BOOSTER_FIELDS = [
  {
    field: "similarity_threshold",
    label: "Exclude low scoring results",
    section: "Threshold for excluding results",
  },
  {
    field: "recency",
    label: "Boost score for recency",
    section: "Adjust Weights for Contextual Factors",
  },
  {
    field: "prevalence",
    label: "Boost score for prevalence",
    section: "Adjust Weights for Contextual Factors",
  },
  {
    field: "endorsed",
    label: "Boost score if endorsed",
    section: "Adjust Weights for Curated Content",
  },
  {
    field: "favorited",
    label: "Boost score if favorited",
    section: "Adjust Weights for User's Actions",
  },
  {
    field: "bookmarked",
    label: "Boost score if bookmarked",
    section: "Adjust Weights for User's Actions",
  },
];

export const SuggestionRankingForm = memo(
  ({ appId }: SuggestionRankingFormProps) => {
    const styles = useSuggestionRankingFormStyles();
    const [storedRankingOptions, setStoredRankingOptions] = useAppData<Record<string, any>>(appId, "rankingOptions");
    const [isRankingOpened, setIsRankinOpened] = useAppData<boolean>(appId, "isRankingOpened");

    const [rankingOptions, setRankingOptions] = useState<Record<string, any>>(
      () => storedRankingOptions ?? DEFAULT_RANKING_OPTIONS
    );

    const rankCount = useMemo(
      () => _.sumBy(_.values(rankingOptions), ({ enabled }) => (enabled ? 1 : 0)),
      [rankingOptions]
    );

    const onUpdateSearchClick = () => {
      setIsRankinOpened(false);
      setStoredRankingOptions(rankingOptions);
    };

    const resetRankingToDefault = () => {
      setRankingOptions(DEFAULT_RANKING_OPTIONS);
    };

    const onRankingChange = (field: string, values: Record<string, any>) => {
      setRankingOptions({
        ...rankingOptions,
        [field]: values,
      });
    };

    return (
      <Popover
        open={isRankingOpened}
        onOpenChange={(_ev, data) => setIsRankinOpened(data.open as boolean)}
        inline
        size="large"
        withArrow
        positioning={"below-start"}
      >
        <GenericPopoverPanel
          content="Adjust ranking of results"
          isOpen={isRankingOpened}
          count={rankCount}
          icon={<TuneIcon color={isRankingOpened ? tokens.colorBrandBackground : ""} />}
        />
        <PopoverSurface className={styles.filterContainerSurface}>
          <div className={styles.formContainer}>
            <div className={styles.toolbarContainer}>
              <Title3>Adjust Ranking</Title3>
            </div>
            <div className={mergeClasses(styles.filterForm)}>
              {BOOSTER_FIELDS.map(({ field, label, section }, index) => {
                const showDivider = index === 0 || BOOSTER_FIELDS[index - 1].section !== section;
                return (
                  <div key={field} className={styles.filterFormField}>
                    {showDivider && <Divider>{section}</Divider>}
                    <RenderRankingBooster
                      field={field}
                      label={label}
                      value={rankingOptions[field]}
                      onChange={(values) => onRankingChange(field, values)}
                    />
                  </div>
                );
              })}
            </div>
            <ActionButtons
              className={mergeClasses(styles.formActionContainer)}
              onUpdate={onUpdateSearchClick}
              onReset={resetRankingToDefault}
              updateLabel="Update Results"
              resetLabel="Reset to default"
              haveWidth={true}
              btnClassName={styles.formActionBtn}
            />
          </div>
        </PopoverSurface>
      </Popover>
    );
  },
  //   dose not it mean “always re-render,” ? why do this and at the same time use memo?
  (_prev, _next) => false
);
